import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Promotions } from 'src/app/shared/interfaces/promotions/promotions';

@Injectable({
  providedIn: 'root',
})
export class PromotionDetailsService {
  private readonly storageKey = 'promotionDetails';

  private readonly promotionDetailsSubject: BehaviorSubject<Promotions | null> =
    new BehaviorSubject<Promotions | null>(this.loadFromStorage());

  promotionDetails$ = this.promotionDetailsSubject.asObservable();

  constructor() {
    this.syncWithStorage();
  }

  /**
   * Retrieves the last stored promotion details.
   * @returns The stored promotion details or null if not set.
   */
  getPromotionDetails(): Promotions | null {
    return this.promotionDetailsSubject.getValue();
  }

  /**
   * Stores promotion details in both memory and sessionStorage.
   * @param promotion The promotion data to store.
   */
  setPromotionDetails(promotion: Promotions): void {
    this.promotionDetailsSubject.next(promotion);
    sessionStorage.setItem(this.storageKey, JSON.stringify(promotion));
  }

  /**
   * Clears the stored promotion details.
   */
  clearPromotionDetails(): void {
    this.promotionDetailsSubject.next(null);
    sessionStorage.removeItem(this.storageKey);
  }

  /**
   * Loads stored data from sessionStorage on service creation.
   * @returns The previously stored promotion details, if available.
   */
  private loadFromStorage(): Promotions | null {
    const storedData = sessionStorage.getItem(this.storageKey);
    return storedData ? JSON.parse(storedData) : null;
  }

  /**
   * Ensures the service stays in sync with sessionStorage on refresh.
   */
  private syncWithStorage(): void {
    window.addEventListener('storage', () => {
      const storedData = this.loadFromStorage();
      this.promotionDetailsSubject.next(storedData);
    });
  }
}
